import { Directive, AfterViewInit  } from '@angular/core';
import { ElementRef, NgZone } from '@angular/core';
import { Output, EventEmitter} from '@angular/core';

/**
 * @Directive TouchDirective
 *
 * A custom Angular directive to handle touch and click events on an element.
 * This directive emits events for various touch and click interactions
 * outside of Angular's zone to improve performance.
 *
 * @selector [uppTouch]
 */
@Directive({
    selector: '[uppTouch]'
})
export class TouchDirective implements AfterViewInit {

    /**
     * Constructor
     *
     * @param el - Reference to the host DOM element where the directive is applied.
     * @param zone - Angular's NgZone service for running code inside or outside the Angular zone.
     */    
    constructor(private el: ElementRef, private zone: NgZone) {
        // nothing to do
    }

    /**
     * Lifecycle Hook: ngAfterViewInit
     *
     * Runs after the view has been initialized. Sets up event listeners
     * for various touch and click events on the host element.
     */    
    ngAfterViewInit() {
        this.zone.runOutsideAngular(() => {
            this.el.nativeElement.addEventListener('click', (event: MouseEvent) => {
                this._onTouchClick(event);
            }, { passive: true });

            this.el.nativeElement.addEventListener('touchstart', (event: TouchEvent) => {
                this._onTouchStart(event);
            }, { passive: true });

            this.el.nativeElement.addEventListener('touchend', (event: TouchEvent) => {
                this._onTouchLeave(event);
            }, { passive: true });

            this.el.nativeElement.addEventListener('touchcancel', (event: TouchEvent) => {
                this._onTouchLeave(event);
            }, { passive: true });

            this.el.nativeElement.addEventListener('touchleave', (event: TouchEvent) => {
                this._onTouchLeave(event);
            }, { passive: true });

            this.el.nativeElement.addEventListener('touchmove', (event: TouchEvent) => {
                this._onTouchLeave(event);
            }, { passive: true });

        });
    }

    /**
     * EventEmitter: TouchClick
     *
     * Emits when a `click` event is triggered on the host element.
     */
    @Output() TouchClick = new EventEmitter<any>();  

    /** @internal */
    private _onTouchClick(event: MouseEvent) {
        this.zone.run(() => {
            this.TouchClick.emit(event);
        });        
    }

    /**
     * EventEmitter: TouchStart
     *
     * Emits when a `touchstart` event is triggered on the host element.
     */    
    @Output() TouchStart = new EventEmitter<any>();  

    /** @internal */
    private _onTouchStart(event: TouchEvent) {
        this.zone.run(() => {
            this.TouchStart.emit(event);
        });        
    }

    /**
     * EventEmitter: TouchLeave
     *
     * Emits when a `touchend`, `touchcancel`, `touchleave`, or `touchmove` event is triggered
     * on the host element.
     */    
    @Output() TouchLeave = new EventEmitter<any>();  

    /** @internal */
    private _onTouchLeave(event: TouchEvent) {
        this.zone.run(() => {
            this.TouchLeave.emit(event);
        });        
    }
}